import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import type { DrivingLimitStatus } from '../api/apiService';
import { formatLimitHours } from './drivingLimit';

// Local notifications for the rolling 24h driving limit (Lei 45/2018, art. 13).
// Production runs no Celery beat, so the server cannot push when a threshold is
// crossed mid-drive; the app schedules them from the server's projection and
// reschedules when that projection changes (see RESCHEDULE_TOLERANCE_MS).
export const DRIVING_LIMIT_WARNING_NOTIFICATION_ID = 'driving-limit-warning';
export const DRIVING_LIMIT_REACHED_NOTIFICATION_ID = 'driving-limit-reached';


const cancelById = async (identifier: string) => {
  try {
    await Notifications.cancelScheduledNotificationAsync(identifier);
  } catch (error) {
    console.warn('Failed to cancel driving limit notification', { identifier, error });
  }
};

export const cancelDrivingLimitNotifications = async () => {
  await Promise.all([
    cancelById(DRIVING_LIMIT_WARNING_NOTIFICATION_ID),
    cancelById(DRIVING_LIMIT_REACHED_NOTIFICATION_ID),
  ]);
};

const schedule = async (identifier: string, seconds: number, title: string, body: string): Promise<boolean> => {
  try {
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: { title, body, sound: true },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
        seconds: Math.max(1, Math.round(seconds)),
        repeats: false,
      },
    });
    return true;
  } catch (error) {
    console.warn('Failed to schedule driving limit notification', { identifier, error });
    return false;
  }
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface PlannedNotification {
  identifier: string;
  seconds: number;
  dueAtMs: number;
  title: string;
  body: string;
}

// ALL-174 decision B (Ronald, 04/11): no SCHEDULE_EXACT_ALARM, so Android
// delivers these as inexact alarms inside a window. Cancelling and
// rescheduling on every 30 s status poll restarts that window and the warning
// may never fire. Reschedule only when the plan changes: another notification,
// another text, or a due time that moved by more than this tolerance (a few
// minutes of delay are accepted).
export const RESCHEDULE_TOLERANCE_MS = 2 * 60 * 1000;

// What was last handed to the OS; null when nothing is scheduled.
let scheduledPlan: PlannedNotification[] | null = null;

const samePlan = (a: PlannedNotification[], b: PlannedNotification[]) =>
  a.length === b.length
  && a.every((item, index) => {
    const other = b[index];
    return item.identifier === other.identifier
      && item.title === other.title
      && item.body === other.body
      && Math.abs(item.dueAtMs - other.dueAtMs) <= RESCHEDULE_TOLERANCE_MS;
  });

const planFor = (
  limit: DrivingLimitStatus | null | undefined,
  isDriving: boolean,
  t: Translate,
  language: string | undefined,
  nowMs: number,
): PlannedNotification[] => {
  if (!limit || !isDriving || !limit.is_driving) return [];

  const hours = formatLimitHours(limit.limit_seconds, language);
  // ALL-174: per-day mode says «hoje» instead of «nas últimas 24 h».
  const copy = limit.mode === 'calendar_day' ? 'drivingLimit.day' : 'drivingLimit';
  const plan: PlannedNotification[] = [];

  if (limit.seconds_until_warning != null && limit.seconds_until_warning > 0) {
    // Time from the warning to the limit; with a rolling window it can exceed
    // the configured lead because older driving leaves the window meanwhile.
    const minutes = Math.round(
      ((limit.seconds_until_limit ?? limit.seconds_until_warning + limit.warning_lead_seconds) -
        limit.seconds_until_warning) / 60,
    );
    plan.push({
      identifier: DRIVING_LIMIT_WARNING_NOTIFICATION_ID,
      seconds: limit.seconds_until_warning,
      dueAtMs: nowMs + limit.seconds_until_warning * 1000,
      title: t('drivingLimit.warningTitle'),
      body: t(`${copy}.warningBody`, { minutes, hours }),
    });
  }
  if (limit.seconds_until_limit != null && limit.seconds_until_limit > 0) {
    plan.push({
      identifier: DRIVING_LIMIT_REACHED_NOTIFICATION_ID,
      seconds: limit.seconds_until_limit,
      dueAtMs: nowMs + limit.seconds_until_limit * 1000,
      title: t('drivingLimit.reachedTitle'),
      body: t(`${copy}.reachedBody`, { hours }),
    });
  }
  return plan;
};

const applyStatus = async (
  limit: DrivingLimitStatus | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
) => {
  const nowMs = Date.now();
  const plan = planFor(limit, isDriving, t, language, nowMs);
  if (plan.length > 0 && scheduledPlan && samePlan(plan, scheduledPlan)) return;

  // Once a threshold is crossed the server stops projecting that notification,
  // but its inexact alarm may still be inside the delivery window (seen on the
  // emulator). Cancelling it then means the driver is never warned, so while
  // driving continues keep alarms that are already due.
  const stillDriving = Boolean(limit && isDriving && limit.is_driving);
  if (stillDriving && scheduledPlan) {
    const kept = scheduledPlan.filter((item) => plan.some((next) => next.identifier === item.identifier));
    const droppedAreDue = scheduledPlan.every((item) => kept.includes(item)
      || item.dueAtMs <= nowMs + RESCHEDULE_TOLERANCE_MS);
    if (droppedAreDue && samePlan(plan, kept)) {
      scheduledPlan = plan;
      return;
    }
  }

  scheduledPlan = null;
  await cancelDrivingLimitNotifications();
  if (plan.length === 0) return;

  const results = [];
  for (const item of plan) {
    results.push(await schedule(item.identifier, item.seconds, item.title, item.body));
  }
  // A failed schedule is retried on the next status refresh.
  scheduledPlan = results.every(Boolean) ? plan : null;
};

// Syncs run one after another: a poll's sync still awaiting its cancel must
// not schedule after a later stop-driving sync has already cancelled.
let syncQueue: Promise<void> = Promise.resolve();

/**
 * Bring the scheduled notifications in line with the latest server status.
 * Only future crossings are scheduled; a threshold already passed is shown
 * in-app instead, so a refresh never re-notifies the same state. Passing no
 * status (or not driving) only cancels.
 */
export const syncDrivingLimitNotifications = (
  limit: DrivingLimitStatus | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
): Promise<void> => {
  if (Platform.OS === 'web') return Promise.resolve();
  syncQueue = syncQueue.then(() => applyStatus(limit, isDriving, t, language)).catch(() => undefined);
  return syncQueue;
};
